import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map, startWith } from 'rxjs';

import { MasterModule } from '../../../../features/settings/models/master-module.model';
import { AuthService } from '../../../../core/auth/auth.service';
import { PortalMenuService } from '../../../services/portal-menu.service';

@Component({
  selector: 'app-portal-sidebar',
  standalone: true,
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive, TranslatePipe],
  templateUrl: './portal-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './portal-sidebar.component.scss',
})
export class PortalSidebarComponent {
  private readonly authService = inject(AuthService);
  private readonly portalMenuService = inject(PortalMenuService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly supportedIconNames = new Set([
    'document',
    'edit',
    'file-text',
    'folders',
    'diagram-3',
    'grid',
    'help-circle',
    'id-card',
    'plus',
    'settings',
    'shield',
    'sitemap',
    'users',
  ]);

  readonly menuModules = computed(() =>
    this.portalMenuService
      .modules()
      .filter((module) => (module.parentId === null || !this.portalMenuService.modules().some((parent) => parent.id === module.parentId)) && module.isActive && module.isMenu && module.route !== '/dashboard')
      .sort(this.sortModules),
  );
  readonly canViewDashboard = computed(() => this.authService.can('dashboard'));

  readonly collapsed = input(false);
  readonly expandRequested = output<void>();
  readonly expandedParents = signal<Set<number>>(new Set());
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  constructor() {
    effect(() => this.expandParentForActiveRoute(this.currentUrl(), this.menuModules()));
  }

  childrenFor(parentId: number): MasterModule[] {
    return this.portalMenuService
      .modules()
      .filter((module) => module.parentId === parentId && module.isActive && module.isMenu)
      .sort(this.sortModules);
  }

  canOpen(module: MasterModule): boolean {
    return !!module.route && module.code !== 'settings' && this.authService.hasAnyPermission(module.code);
  }

  toggleParent(parentId: number): void {
    if (this.collapsed()) {
      this.expandedParents.update((expanded) => new Set([...expanded, parentId]));
      this.expandRequested.emit();
      return;
    }

    this.expandedParents.update((expanded) => {
      const next = new Set(expanded);
      next.has(parentId) ? next.delete(parentId) : next.add(parentId);
      return next;
    });
  }

  isExpanded(parentId: number): boolean {
    return this.expandedParents().has(parentId);
  }

  iconClass(icon: string | null): string {
    const iconName = icon && this.supportedIconNames.has(icon) ? icon : 'default';
    return `portal-nav__icon portal-nav__db-icon portal-nav__db-icon--${iconName}`;
  }

  menuLabel(module: MasterModule): string {
    const translated = this.translate.instant(module.labelKey);
    return translated === module.labelKey ? module.moduleName : translated;
  }

  private expandParentForActiveRoute(url: string, _parents: MasterModule[]): void {
    const modules = this.portalMenuService.modules();
    const active = modules.filter((module) => this.isRouteActive(module.route, url))
      .sort((left, right) => right.route.length - left.route.length)[0];
    if (!active) return;
    const ancestors = new Set<number>();
    let parentId = active.parentId;
    while (parentId !== null && !ancestors.has(parentId)) {
      ancestors.add(parentId);
      parentId = modules.find((module) => module.id === parentId)?.parentId ?? null;
    }
    this.expandedParents.update((expanded) => new Set([...expanded, ...ancestors]));
  }

  private isRouteActive(route: string, url: string): boolean {
    return !!route && (url === route || url.startsWith(`${route}/`));
  }

  private readonly sortModules = (left: MasterModule, right: MasterModule): number =>
    left.sortOrder - right.sortOrder || left.moduleName.localeCompare(right.moduleName);
}
